import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaLock, FaCheck, FaTimes, FaShieldAlt } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import './ChangePassword.css'; // Reusing the styling from ChangePassword

function ResetPassword() {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Check if there's a hash fragment in the URL (which contains the access token)
    const [hashToken, setHashToken] = useState('');
    
    useEffect(() => {
        // Extract hash token from URL if it exists
        const hash = location.hash;
        let foundToken = '';
        if (hash) {
            foundToken = hash.substring(1); // Remove the # character
        } else {
            // Check if the token is in the query parameters (Supabase may use query params)
            const params = new URLSearchParams(location.search);
            const token = params.get('token');
            if (token) {
                foundToken = token;
            }
        }
        if (foundToken) {
            setHashToken(foundToken);
            setError(null); // Clear any previous error if token is found
        } else {
            // Only set the error if there is no token at all
            setError('No se encontró un token válido de restablecimiento. Por favor solicite un nuevo correo de restablecimiento.');
        }
    }, [location]);

    // Password validation states
    const [validations, setValidations] = useState({
        hasUppercase: false,
        hasNumber: false,
        hasMinLength: false,
        passwordsMatch: false
    });

    // Check validation whenever password inputs change
    useEffect(() => {
        const hasUppercase = /[A-Z]/.test(newPassword);
        const hasNumber = /[0-9]/.test(newPassword);
        const hasMinLength = newPassword.length >= 6;
        const passwordsMatch = newPassword === confirmPassword;

        setValidations({
            hasUppercase,
            hasNumber,
            hasMinLength,
            passwordsMatch: passwordsMatch && confirmPassword !== ''
        });

        // Calculate password strength (0-100)
        let strength = 0;
        if (newPassword.length > 0) {
            // Base points for having anything
            strength += 20;
            
            // Add points for each validation
            if (hasUppercase) strength += 20;
            if (hasNumber) strength += 20;
            if (hasMinLength) strength += 20;
            
            // Extra points for length beyond minimum
            if (newPassword.length > 8) strength += Math.min((newPassword.length - 8) * 5, 20);
            
            // Cap at 100
            strength = Math.min(strength, 100);
        }
        
        setPasswordStrength(strength);

        // All fields must be filled and validation must pass to enable button
        const allFieldsFilled = newPassword && confirmPassword;
        const allValidationsPassed = hasUppercase && hasNumber && hasMinLength && passwordsMatch;

        setIsButtonDisabled(!allFieldsFilled || !allValidationsPassed);
    }, [newPassword, confirmPassword]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);

        // Final validation check before submission
        if (!validations.hasUppercase || !validations.hasNumber || !validations.hasMinLength) {
            setError('La contraseña nueva debe tener al menos una letra mayúscula, un número y 6 caracteres.');
            return;
        }

        if (!validations.passwordsMatch) {
            setError('Las contraseñas nuevas no coinciden.');
            return;
        }

        setLoading(true);

        try {
            // Update the password using the token from URL
            const { error } = await supabase.auth.updateUser({
                password: newPassword
            });

            if (error) throw error;

            // Clear form and show success message
            setNewPassword('');
            setConfirmPassword('');
            setSuccess(true);
            
            // Redirect to login after success
            setTimeout(() => {
                navigate('/');
            }, 3000);

        } catch (error) {
            console.error('Error changing password:', error);
            setError(error.message || 'Error al restablecer la contraseña. El enlace puede haber expirado. Por favor solicite un nuevo correo de restablecimiento.');
        } finally {
            setLoading(false);
        }
    };

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: { 
                duration: 0.5,
                when: "beforeChildren",
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
    };

    // Get strength color
    const getStrengthColor = () => {
        if (passwordStrength < 40) return '#dc3545';
        if (passwordStrength < 70) return '#ffc107';
        return '#28a745';
    };

    // Get strength label
    const getStrengthLabel = () => {
        if (passwordStrength < 40) return 'Débil';
        if (passwordStrength < 70) return 'Buena';
        return 'Excelente';
    };

    return (
        <div className="cp-container">
            <div className="cp-content">
                <motion.div 
                    className="cp-card"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    <motion.div className="cp-header" variants={itemVariants}>
                        <div className="cp-icon-container">
                            <FaShieldAlt className="cp-shield-icon"/>
                        </div>
                        <h1>Restablecer Contraseña</h1>
                        <p className="cp-subtitle">Crea una nueva contraseña para tu cuenta</p>
                    </motion.div>

                    <AnimatePresence>
                        {success && (
                            <motion.div 
                                className="cp-success-message"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <FaCheck className="cp-message-icon" />
                                ¡Contraseña restablecida con éxito! Serás redirigido al inicio de sesión.
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                        {error && (
                            <motion.div 
                                className="cp-error-message"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <FaTimes className="cp-message-icon" />
                                {error}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <motion.form onSubmit={handleSubmit} variants={itemVariants}>
                        <motion.div className="cp-form-group" variants={itemVariants}>
                            <label htmlFor="new-password">
                                <FaLock className="cp-input-icon" />
                                Nueva contraseña
                            </label>
                            <div className="cp-input-container">
                                <input
                                    id="new-password"
                                    type={showNewPassword ? "text" : "password"}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                    className="cp-input"
                                />
                                <button
                                    type="button"
                                    className="cp-toggle-button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    tabIndex="-1"
                                >
                                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                            
                            {newPassword && (
                                <motion.div 
                                    className="cp-password-strength"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className="cp-strength-label">
                                        Fortaleza: <span style={{ color: getStrengthColor() }}>{getStrengthLabel()}</span>
                                    </div>
                                    <div className="cp-strength-bar-container">
                                        <motion.div 
                                            className="cp-strength-bar"
                                            initial={{ width: 0 }}
                                            animate={{ width: `${passwordStrength}%` }}
                                            style={{ backgroundColor: getStrengthColor() }}
                                        />
                                    </div>
                                </motion.div>
                            )}
                            
                            {newPassword && (
                                <motion.div 
                                    className="cp-requirements"
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className={validations.hasMinLength ? "cp-requirement-met" : "cp-requirement"}>
                                        {validations.hasMinLength ? <FaCheck /> : <FaTimes />}
                                        Al menos 6 caracteres
                                    </div>
                                    <div className={validations.hasUppercase ? "cp-requirement-met" : "cp-requirement"}>
                                        {validations.hasUppercase ? <FaCheck /> : <FaTimes />}
                                        Al menos una letra mayúscula
                                    </div>
                                    <div className={validations.hasNumber ? "cp-requirement-met" : "cp-requirement"}>
                                        {validations.hasNumber ? <FaCheck /> : <FaTimes />}
                                        Al menos un número
                                    </div>
                                </motion.div>
                            )}
                        </motion.div>

                        <motion.div className="cp-form-group" variants={itemVariants}>
                            <label htmlFor="confirm-password">
                                <FaLock className="cp-input-icon" />
                                Confirmar nueva contraseña
                            </label>
                            <div className="cp-input-container">
                                <input
                                    id="confirm-password"
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                    className="cp-input"
                                />
                                <button
                                    type="button"
                                    className="cp-toggle-button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    tabIndex="-1"
                                >
                                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                            {confirmPassword && !validations.passwordsMatch && (
                                <motion.div 
                                    className="cp-mismatch"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <FaTimes /> Las contraseñas no coinciden
                                </motion.div>
                            )}
                        </motion.div>

                        <motion.button
                            type="submit"
                            className={`cp-button ${isButtonDisabled ? 'disabled' : ''}`}
                            disabled={isButtonDisabled || loading}
                            variants={itemVariants}
                            whileHover={!isButtonDisabled ? { scale: 1.03 } : {}}
                            whileTap={!isButtonDisabled ? { scale: 0.98 } : {}}
                        >
                            {loading ? 'Actualizando...' : 'Restablecer Contraseña'}
                        </motion.button>
                    </motion.form>
                </motion.div>
            </div>
        </div>
    );
}

export default ResetPassword;